import { useEffect, useState } from 'react';
import { getContentPath } from '@/utils/functions';
import { bundleFileNames } from '../lib/schedule';
import { platform } from '@/platform';
import { useLogger } from './useLogger';

/**
 * Keeps every media file of the bundle's horizon (plus `extraUrls`, e.g. the tenant's idle
 * images) available offline and drops files no longer scheduled: in the content cache served by
 * the service worker on the hosted player, on device storage in the packaged apps (see
 * src/platform). Only the first warm-up after boot is reported as `isWarming`; later passes run
 * in the background and skip files already stored.
 */
export const useContentPreload = ({ bundle, tid, extraUrls = [] }) => {
  const [isWarming, setIsWarming] = useState(false);
  const [warmedOnce, setWarmedOnce] = useState(false);
  const { LogToExternal } = useLogger();

  useEffect(() => {
    if (!bundle || !tid) return;

    let cancelled = false;
    const urls = [
      ...bundleFileNames(bundle).map((name) => getContentPath(tid) + name),
      ...extraUrls.filter(Boolean),
    ];
    if (!warmedOnce) setIsWarming(true);

    platform.media
      .sync(urls)
      .then((failed) => {
        // Prune only after a complete pass (e.g. not while offline with gaps in the cache).
        if (!cancelled && failed.length === 0)
          return platform.media.prune(urls);
        if (failed.length)
          LogToExternal('info', `Preload: ${failed.length} file(s) not cached`);
      })
      .catch((error) => LogToExternal('error', error))
      .finally(() => {
        if (cancelled) return;
        setIsWarming(false);
        setWarmedOnce(true);
      });

    return () => {
      cancelled = true;
    };
  }, [bundle, tid, extraUrls.join('|')]);

  return { isWarming };
};
